/**
 * FeedbackAnswerBadge — AppShell 헤더 문의 답변 알림 배지 (queue #691 · docs/biz-specs/common-showcase.md
 * §UAT-2026-07-14-R691 §R691-3)
 *
 * `useMyFeedback({page:0,size:50}, {refetchInterval:60_000, ...})` 로 본인 문의 이력을 60s
 * 폴링해, `status==='ANSWERED' && answeredAt > lastSeenAt`(`useFeedbackSeen`) 조건을 만족하는
 * 새 답변 수를 FE 파생 계산한다(BE 신규 endpoint 0 · §R691-2(a)). count===0(또는 로딩/오류) 이면
 * 미렌더(§R682 `ScaffoldCartBadge` 와 동일한 "0=숨김" 규약 · 헤더 소란 방지 위한 무음 폴백).
 * 클릭 시 `/showcase?feedback=mine` 으로 이동한다. `_shared/AuthLayout.tsx` headerActions 슬롯에
 * `ScaffoldCartBadge` 옆에 주입된다.
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMyFeedback } from '../hooks/useMyFeedback';
import { useFeedbackSeen } from '../hooks/useFeedbackSeen';

// §R691-2(a): 답변이 오래된 문의에 달릴 수 있어 기본 20 대신 50 으로 조회(최신 20건 밖 답변
// 누락 경계 축소 · 서버 부하는 60s 폴링 × 소규모 페이지로 무시 가능).
const BADGE_QUERY_FILTER = { page: 0, size: 50 };
const BADGE_QUERY_OPTIONS = {
  refetchInterval: 60_000,
  refetchIntervalInBackground: false,
  staleTime: 30_000,
};

export function FeedbackAnswerBadge() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lastSeenAtMs } = useFeedbackSeen();
  const mineQuery = useMyFeedback(BADGE_QUERY_FILTER, BADGE_QUERY_OPTIONS);

  // loading/error → 무음 폴백(§R691-3(b)): 배지 미렌더, 토스트/ErrorState 없음.
  if (mineQuery.isLoading || mineQuery.isError || !mineQuery.data) return null;

  const count = mineQuery.data.content.filter(
    (item) => item.status === 'ANSWERED' && item.answeredAt != null && Date.parse(item.answeredAt) > lastSeenAtMs
  ).length;

  if (count === 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/showcase?feedback=mine')}
      aria-label={t('showcase.feedback.notify.badge.aria', { count })}
      title={t('showcase.feedback.notify.title', { count })}
      data-testid="feedback-answer-badge"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        background: 'none',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-sm)',
        padding: '4px var(--spacing-2)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        color: 'var(--color-brand-primary)',
      }}
    >
      <span aria-hidden="true">🔔</span>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 16,
          height: 16,
          padding: '0 4px',
          borderRadius: 'var(--radius-full)',
          background: 'var(--color-brand-primary)',
          color: 'var(--color-text-inverse)',
          fontSize: 10,
          fontWeight: 700,
        }}
      >
        {count}
      </span>
    </button>
  );
}
